/**
 * `TypedConfirmDialog` — destructive-confirm modal that gates the
 * primary action behind the user typing a verification string
 * (typically the resource's domain or ID). The pattern matches
 * GitHub's "repository delete" and Stripe's "delete account" UX:
 * the Confirm button stays disabled until the typed value is a
 * strict-equality match for `expected`.
 *
 * Used by the admin for hard-deleting an account (typed = primary
 * domain or account_id) and (optionally) other irreversible flows.
 * Shares the surface/border tokens of `<ConfirmDialog />` but has its
 * own component so the typed-gate logic doesn't have to be smeared
 * into the simpler dialog's props.
 *
 * Visual tone: coral primary, since the gate exists only for
 * destructive actions. The dialog itself sits on `C.surface` like
 * the rest of the admin's cards.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface TypedConfirmDialogProps {
  open: boolean;
  title: string;
  body?: ReactNode;
  /**
   * The exact string the user must type to enable the Confirm button.
   * Strict-equality after trimming surrounding whitespace.
   */
  expected: string;
  /** Mono-styled label rendered above the input — usually `'Type "<expected>" to confirm'`. */
  inputLabel?: ReactNode;
  /** Mono-styled placeholder for the input. Defaults to `expected`. */
  inputPlaceholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  pending?: boolean;
  /** Error banner (e.g. "Account has audit history" after a 409). */
  error?: string | null;
  /** Footer below the action row; used to render the 409 → "Suspend instead" affordance. */
  footer?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

export function TypedConfirmDialog({
  open,
  title,
  body,
  expected,
  inputLabel,
  inputPlaceholder,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  pending = false,
  error = null,
  footer,
  onConfirm,
  onCancel,
}: TypedConfirmDialogProps): JSX.Element | null {
  const C = useC();
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset the input every time the dialog opens; a half-typed value
  // from a previous open should never persist into a fresh confirm.
  useEffect(() => {
    if (open) {
      setTyped('');
      // Focus the input on open so the staff member can start typing
      // immediately — destructive flows shouldn't make them click first.
      const id = window.requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  if (!open) return null;

  const matches = typed.trim() === expected.trim() && expected.trim().length > 0;
  const confirmEnabled = matches && !pending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          border: `1px solid ${C.coralFg}`,
          borderRadius: 3,
          width: '100%',
          maxWidth: 480,
          padding: 24,
        }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.coralFg,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Destructive
        </div>
        <h2
          style={{
            fontFamily: sansFont,
            fontSize: 18,
            fontWeight: 500,
            color: C.text,
            marginBottom: body !== undefined ? 12 : 16,
          }}
        >
          {title}
        </h2>
        {body !== undefined ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.body,
              lineHeight: 1.5,
              marginBottom: 16,
            }}
          >
            {body}
          </div>
        ) : null}

        <label
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            display: 'block',
            marginBottom: 6,
          }}
        >
          {inputLabel ?? <>Type <span style={{ color: C.text }}>{expected}</span> to confirm</>}
        </label>
        <input
          ref={inputRef}
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={pending}
          placeholder={inputPlaceholder ?? expected}
          aria-label="Confirmation phrase"
          autoComplete="off"
          spellCheck={false}
          className="w-full px-3 py-2 outline-none"
          style={{
            fontFamily: monoFont,
            fontSize: 13,
            color: C.text,
            background: C.surface2,
            border: `1px solid ${matches ? C.coralFg : C.border}`,
            borderRadius: 2,
            marginBottom: 16,
          }}
        />

        {error !== null && error.length > 0 ? (
          <div
            className="mb-4 p-3"
            style={{
              background: C.coralDim,
              border: `1px solid ${C.coralFg}`,
              borderRadius: 2,
              fontFamily: sansFont,
              fontSize: 12,
              color: C.text,
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.muted,
              background: 'transparent',
              border: `1px solid ${C.border}`,
              padding: '8px 14px',
              borderRadius: 2,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!confirmEnabled}
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              color: confirmEnabled ? C.text : C.muted,
              background: confirmEnabled ? C.coralFg : C.surface2,
              border: 0,
              padding: '8px 14px',
              borderRadius: 2,
              opacity: pending ? 0.6 : 1,
              cursor: confirmEnabled ? 'pointer' : 'not-allowed',
            }}
          >
            {pending ? 'Deleting…' : confirmLabel}
          </button>
        </div>

        {footer !== undefined ? (
          <div style={{ marginTop: 16 }}>{footer}</div>
        ) : null}
      </div>
    </div>
  );
}
